import axios from 'axios';

// --- Axios client ---
export const client = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.ledgery.test/api',
    withCredentials: true,
    withXSRFToken: true,
});

client.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
client.defaults.headers.common['Accept'] = 'application/json';
client.defaults.headers.common['Content-Type'] = 'application/json';

// Unwrap { success, data, error } envelope
client.interceptors.response.use(
    (response) => {
        // 204 No Content — nothing to unwrap
        if (response.status === 204 || !response.data) {
            return response;
        }
        // Binary responses (e.g. file downloads) — return as-is
        if (response.data instanceof Blob) {
            return response;
        }
        const body = response.data;
        if (body.success !== undefined) {
            // API envelope response
            response.data = body.data;
        } else {
            response.data = body;
        }
        return response;
    },
    async (error) => {
        const originalConfig = error.config;

        // 419 CSRF token mismatch — refresh token and retry once
        if (error.response?.status === 419 && !originalConfig._csrfRetried) {
            originalConfig._csrfRetried = true;
            try {
                await client.get('/v1/csrf-token');
            } catch {
                const err = new Error('Session expired. Please refresh the page.');
                err.code = 'SESSION_EXPIRED';
                err.status = 419;
                throw err;
            }
            return client(originalConfig);
        }

        // No network response (offline / DNS failure / timeout)
        if (!error.response) {
            const err = new Error('Network error. Check your connection and try again.');
            err.code = 'NETWORK_ERROR';
            err.status = null;
            throw err;
        }

        // API envelope error response
        if (error.response.data?.error) {
            const apiError = error.response.data.error;
            const err = new Error(apiError.message || 'Something went wrong.');
            err.code = apiError.code;
            err.status = error.response.status;
            err.errors = apiError.errors ?? null;
            err.response = error.response;

            // 401 UNAUTHORIZED — redirect to login for all requests except the
            // auth probe (which uses _skipAuthRedirect to handle null itself)
            if (err.status === 401 && !originalConfig?._skipAuthRedirect) {
                window.location.replace('/login');
            }

            throw err;
        }

        // Non-envelope HTTP error (unexpected response shape)
        const err = new Error(`Request failed (HTTP ${error.response.status}).`);
        err.code = 'HTTP_ERROR';
        err.status = error.response.status;
        err.errors = null;
        err.response = error.response;

        if (err.status === 401 && !originalConfig?._skipAuthRedirect) {
            window.location.replace('/login');
        }

        throw err;
    }
);
